import React, { useState, useEffect, useRef } from 'react';
import { Close, CheckCircle } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { confirmWithdrawal, resendWithdrawalToken } from '../../services/withdrawalService';
import WithdrawSuccessModal from './WithdrawSuccessModal';

const ConfirmWithdrawalModal = ({ show, onClose, expires_in, trans_id, onSuccess }) => {
    const { selectedOrganization } = useAuth();
    const [expired, setExpired] = useState(false);
    const [openSuccessModal, setOpenSuccessModal] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [token, setToken] = useState('');
    const [remainingTime, setRemainingTime] = useState(0);
    const [resending, setResending] = useState(false);
    const [resendMessage, setResendMessage] = useState(null);
    // Échéance courante du code : repoussée d'une durée de validité à chaque renvoi
    const [expiryTime, setExpiryTime] = useState(null);
    const validityRef = useRef(null);

    useEffect(() => {
        if (!show || !expires_in) return;
        const initial = typeof expires_in === 'string' ? new Date(expires_in).getTime() : expires_in;
        validityRef.current = Math.max(60 * 1000, initial - Date.now());
        setExpiryTime(initial);
        setExpired(false);
        setResendMessage(null);
    }, [show, expires_in]);

    useEffect(() => {
        if (!show || !expiryTime) return;

        const updateRemaining = () => {
            const now = Date.now();

            const diff = Math.max(0, Math.floor((expiryTime - now) / 1000)); // en secondes
            setRemainingTime(diff);
            
            if (diff === 0) {
                setExpired(true);
            }
        };

        updateRemaining(); // initial

        const interval = setInterval(() => {
            updateRemaining();
        }, 1000);

        return () => clearInterval(interval);
    }, [show, expiryTime]);

    const handleResend = async () => {
        if (!trans_id || resending) return;
        setResending(true);
        setResendMessage(null);
        try {
            const data = await resendWithdrawalToken(trans_id);
            setExpiryTime(Date.now() + (validityRef.current || 5 * 60 * 1000));
            setExpired(false);
            setToken('');
            setResendMessage({ ok: true, text: data?.message || 'Code de confirmation renvoyé.' });
        } catch (error) {
            setResendMessage({ ok: false, text: error.message });
        } finally {
            setResending(false);
        }
    };

    const formatTime = (seconds) => {
        // Sur mobile: formatTime prend des secondes (pas des millisecondes)
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}s`;
    };

    const onCloseSuccessModal = () => {
        setExpired(false);
        setToken('');
        setOpenSuccessModal(false);
        onClose();
        if (onSuccess) {
            onSuccess();
        }
    };

    const handleConfirm = async () => {
        if (token.length !== 5 || isNaN(token)) {
            alert('Le code doit contenir exactement 5 chiffres.');
            return;
        }
        
        try {
            setSubmitting(true);

            const confirmData = {
                token: token,
                owner: selectedOrganization?.id
            };

            const response = await confirmWithdrawal(confirmData);
            
            if (response) {
                if (response.error) {
                    alert(response.message || 'Erreur lors de la confirmation');
                    if (response.target === 'token_expired_Error') {
                        setExpired(true);
                    }
                    return;
                }
                setOpenSuccessModal(true);
            }
        } catch (error) {
            console.error('Error confirming withdrawal:', error);
            alert(error.message || 'Erreur lors de la confirmation');
        } finally {
            setSubmitting(false);
        }
    };

    if (!show) return null;

    return (
        <>
            <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-xl font-bold text-gray-900">Demande de retrait</h3>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <Close className="text-gray-500" />
                        </button>
                    </div>

                    <div className="space-y-6">
                        <div className="text-center">
                            <h4 className="text-2xl font-semibold text-gray-900 mb-2">
                                Confirmation de retrait
                            </h4>
                            <p className="text-gray-600">
                                Entrez le code de confirmation reçu par SMS, WhatsApp ou Email.
                            </p>
                        </div>

                        <div className="text-center">
                            <p className="text-sm font-medium text-gray-700">
                                Exp: {formatTime(remainingTime)}
                            </p>
                        </div>

                        <div>
                            <input
                                type="text"
                                value={token}
                                onChange={(e) => {
                                    const text = e.target.value;
                                    if (text.length <= 5 && /^\d*$/.test(text)) {
                                        setToken(text);
                                    }
                                }}
                                keyboardType="numeric"
                                maxLength={5}
                                placeholder="Code à 5 chiffres"
                                className="w-full px-6 py-4 text-center text-2xl font-mono tracking-widest border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                            />
                        </div>

                        <button
                            onClick={handleConfirm}
                            disabled={submitting || token.length !== 5}
                            className="w-full bg-amber-500 text-white py-3 rounded-xl font-semibold hover:bg-amber-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                        >
                            {submitting ? (
                                <>
                                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    <span>Confirmation...</span>
                                </>
                            ) : (
                                <span>Confirmer</span>
                            )}
                        </button>

                        <button
                            type="button"
                            onClick={handleResend}
                            disabled={resending || !trans_id}
                            className="w-full text-center text-amber-600 hover:text-amber-700 font-medium text-sm disabled:opacity-50"
                        >
                            {resending ? 'Envoi du code…' : expired ? 'Code expiré : renvoyer un nouveau code' : 'Renvoyer le code ?'}
                        </button>
                        {resendMessage && (
                            <p className={`text-center text-sm ${resendMessage.ok ? 'text-emerald-600' : 'text-red-600'}`}>
                                {resendMessage.text}
                            </p>
                        )}
                    </div>
                </div>
            </div>

            {/* Modal de succès */}
            {openSuccessModal && (
                <WithdrawSuccessModal
                    show={openSuccessModal}
                    onClose={onCloseSuccessModal}
                />
            )}
        </>
    );
};

export default ConfirmWithdrawalModal;

